/** Unmatched-signal admin report (spec §4.4): GET /oms/ingest/unmatched
 *  (oms_admin-gated). Raw signals whose meter_number had no GIS crosswalk — a
 *  reconciliation worklist. A viewer hitting the admin endpoint gets a 403,
 *  surfaced as an explicit "requires admin" note (degraded-data explicit,
 *  never a blank — §9). */
import { isApiError } from '../../api/client.ts'
import { useUnmatchedSignals, useUnmatchedSummary } from '../../hooks/useOms.ts'
import { formatDateTime } from './omsFormat.tsx'
import { OmsSubNav } from './OmsSubNav.tsx'

export function UnmatchedSignalsPage() {
  const { data, isLoading, isError, error } = useUnmatchedSignals({ limit: 200 })
  const forbidden = isApiError(error) && error.status === 403

  return (
    <div className="flex-1 overflow-auto bg-gray-50">
      <div className="max-w-7xl mx-auto p-4 space-y-4">
        <OmsSubNav />
        <div>
          <h2 className="text-lg font-semibold text-gray-800">Unmatched signals</h2>
          <p className="text-sm text-gray-500">
            Raw outage signals whose meter number could not be resolved to a GIS
            meter. Admin-only.
          </p>
        </div>

        {/* Crosswalk worklist: one row per number */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-3 py-2 border-b border-gray-200">
            <h3 className="text-sm font-semibold text-gray-800">
              Numbers needing a GIS crosswalk
            </h3>
            <p className="text-xs text-gray-500">
              One row per meter number missing from GIS. Fix these in GIS (or add
              an alias) and the outage feed starts grouping their meters
              automatically.
            </p>
          </div>
          <UnmatchedSummaryTable />
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {isLoading ? (
            <div className="text-sm text-gray-400 py-8 text-center">Loading…</div>
          ) : forbidden ? (
            <div className="text-sm text-amber-700 bg-amber-50 py-8 text-center">
              This report requires the OMS admin role.
            </div>
          ) : isError ? (
            <div className="text-sm text-red-600 py-8 text-center">
              Failed to load unmatched signals.
            </div>
          ) : !data || data.length === 0 ? (
            <div className="text-sm text-gray-400 py-8 text-center">
              No unmatched signals — every recent signal resolved to a meter.
            </div>
          ) : (
            <div className="overflow-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-left text-gray-500">
                  <tr className="border-b border-gray-200">
                    <th className="p-2">Meter number</th>
                    <th className="p-2">Source</th>
                    <th className="p-2">Signal</th>
                    <th className="p-2">Event time</th>
                    <th className="p-2">Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map(s => (
                    <tr key={s.id} className="border-b border-gray-50">
                      <td className="p-2 font-mono text-xs">{s.meter_number ?? '--'}</td>
                      <td className="p-2">{s.source}</td>
                      <td className="p-2">{s.signal_type}</td>
                      <td className="p-2">{formatDateTime(s.event_time)}</td>
                      <td className="p-2 text-gray-500">{s.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function UnmatchedSummaryTable() {
  const { data, isLoading, isError, error } = useUnmatchedSummary({ limit: 200 })
  const forbidden = isApiError(error) && error.status === 403
  if (isLoading)
    return <div className="text-sm text-gray-400 py-6 text-center">Loading…</div>
  if (forbidden)
    return (
      <div className="text-sm text-amber-700 bg-amber-50 py-6 text-center">
        This report requires the OMS admin role.
      </div>
    )
  if (isError)
    return (
      <div className="text-sm text-red-600 py-6 text-center">
        Failed to load the worklist.
      </div>
    )
  if (!data || data.length === 0)
    return (
      <div className="text-sm text-gray-400 py-6 text-center">
        No unmatched numbers — every recent signal resolved to a meter.
      </div>
    )
  return (
    <div className="overflow-auto">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 text-left text-gray-500">
          <tr className="border-b border-gray-200">
            <th className="p-2">Meter number</th>
            <th className="p-2 text-right">Signals</th>
            <th className="p-2">First seen</th>
            <th className="p-2">Last seen</th>
            <th className="p-2">Active outage</th>
          </tr>
        </thead>
        <tbody>
          {data.map(r => (
            <tr key={r.meter_number} className="border-b border-gray-50">
              <td className="p-2 font-mono text-xs">{r.meter_number}</td>
              <td className="p-2 text-right">{r.signal_count}</td>
              <td className="p-2">{formatDateTime(r.first_seen)}</td>
              <td className="p-2">{formatDateTime(r.last_seen)}</td>
              <td className="p-2">
                {r.active_event_id != null ? `#${r.active_event_id}` : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
